import React from 'react';
import { useRouter } from 'next/router';

import { LottieLayer } from '@zola/zola-ui/src/animations/LottieLayer/LottieLayer';
import { LottieProvider } from '@zola/zola-ui/src/animations/LottieLayer/LottieContext';

import { useAnimationContext } from 'components/manage/EditWebsite/EditWebsiteAddEffects/components/AnimationContext/AnimationContext';
import { hexToRgb } from '@zola/zola-ui/src/util/color/hexToRgb';

import { useWebsiteThemeContext } from 'components/publicWebsiteV2/context';
import {
  StyledOverlayWrapper,
  StyledGlenardenWrapper,
  StyledViallBottomLeftWrapper,
  StyledViallTopRightWrapper,
  StyledBridlewoodWrapper,
} from './OverlayAnimation.styles';
import Glenarden from './LottieAnimations/Glenardenv2.json';

import {
  customizeMonogram,
  customizeBridlewood,
  customizeCreston,
  updateInitialsForInitialsInAssets,
  customizeLocket,
  customizeViall,
} from './utils';

import { getHeroTitleOverrideStyles } from '../MultiPageHeroContainer/components/MultiPageHeroOnlyText/MultiPageHeroOnlyText.styles';

type OverlayAnimationProps = {
  isSinglePage?: boolean;
  hasBackgroundImage?: boolean;
};

export const OverlayAnimation = (props: OverlayAnimationProps) => {
  const { isSinglePage, hasBackgroundImage } = props;
  const {
    overlayAnimation,
    showAnimationsInEditWebsite,
    overlayAnimationId,
    pageAnimation,
    speed,
  } = useAnimationContext();

  const {
    state: {
      components: { globalHeaderFontValues },
      wedding,
    },
  } = useWebsiteThemeContext();

  const router = useRouter();
  const { preview_type } = router?.query || {};
  const isIosScreenshot = (preview_type as string | undefined)?.toUpperCase() === 'SCREENSHOT';

  const { wedding_date, owner_first_name, partner_first_name } = wedding || {};

  const fontColorOverride = getHeroTitleOverrideStyles(!!hasBackgroundImage);
  const fontColorToUseHex = fontColorOverride.color || globalHeaderFontValues.color;
  const fontColorToUseRGB = hexToRgb(fontColorToUseHex);

  const defaultColorRGB: [number, number, number, number] = fontColorToUseRGB
    ? [fontColorToUseRGB.r / 255, fontColorToUseRGB.g / 255, fontColorToUseRGB.b / 255, 1]
    : [0, 0, 0, 1];

  const { overlay_style: overlayCustomizedStyle, key: overlayAnimationKey } =
    overlayAnimation || {};

  const { color: overlayStyleColor, size, position } = overlayCustomizedStyle || {};
  const customColorRGB = overlayStyleColor ? hexToRgb(overlayStyleColor) : null;

  const sizeInPercent = size ? size * 100 : 50;

  // In lottie colors are represented as arrays with values between 0 and 1
  // so we need to divide by 255 for lottie to understand
  const colorToUseRGB: [number, number, number, number] = customColorRGB
    ? [customColorRGB.r / 255, customColorRGB.g / 255, customColorRGB.b / 255, 1]
    : defaultColorRGB;

  const colorToUseHex = overlayStyleColor || fontColorToUseHex;

  const firstInitial = owner_first_name?.slice(0, 1);
  const secondInitial = partner_first_name?.slice(0, 1);

  if (!showAnimationsInEditWebsite) {
    return null;
  }

  const speedToUse = speed || 1;
  const autoPlay = !isIosScreenshot;
  const showDelay = pageAnimation && autoPlay;

  const getAnimation = () => {
    if (overlayAnimationKey === 'overlay-animation-ampersand') {
      const monogramToUse = customizeMonogram({
        animationKey: 'overlay-animation-ampersand',
        initialLayerOne: '.PartnerInitia_Right 2',
        initialLayerTwo: '.PartnerInitia_Left',
        color: colorToUseRGB,
        firstInitial,
        secondInitial,
      });

      return (
        <StyledOverlayWrapper
          delay={showDelay ? speedToUse : 0}
          key={`${overlayAnimationId}-monogram`}
          isSinglePage={isSinglePage}
          heightPercentage={sizeInPercent}
          position={position}
          style={{ fill: `#${colorToUseHex}` }}
          isMonogram
        >
          <LottieLayer
            data={(monogramToUse as unknown) as Record<string, unknown>}
            autoplay={autoPlay}
            finalFrameValue={249}
          />
        </StyledOverlayWrapper>
      );
    }

    if (overlayAnimationKey === 'overlay-animation-glenarden') {
      return (
        <StyledGlenardenWrapper
          delay={showDelay ? speedToUse : 0}
          key={`${overlayAnimationId}-glenarden`}
          isSinglePage={isSinglePage}
          heightPercentage={sizeInPercent}
        >
          <LottieLayer data={Glenarden} autoplay={autoPlay} finalFrameValue={271} />
        </StyledGlenardenWrapper>
      );
    }
    if (overlayAnimationKey === 'overlay-animation-creston') {
      const crestonToUse = customizeCreston({
        animationKey: 'overlay-animation-creston',
        initialLayerOne: '.PartnerInitial2',
        initialLayerTwo: '.PartnerInitial1',
        color: colorToUseRGB,
        firstInitial,
        secondInitial,
      });

      return (
        <StyledOverlayWrapper
          delay={showDelay ? speedToUse : 0}
          isSinglePage={isSinglePage}
          heightPercentage={sizeInPercent}
          position={position}
          key={`${overlayAnimationId}-creston`}
          style={{ fill: `#${colorToUseHex}` }}
        >
          <LottieLayer
            data={(crestonToUse as unknown) as Record<string, unknown>}
            autoplay={autoPlay}
            finalFrameValue={1450}
          />
        </StyledOverlayWrapper>
      );
    }
    if (overlayAnimationKey === 'overlay-animation-willow') {
      const willowToUse = updateInitialsForInitialsInAssets({
        animationKey: 'overlay-animation-willow',
        initialLayerOne: 'A',
        initialLayerTwo: 'B',
        color: colorToUseRGB,
        firstInitial,
        secondInitial,
        assetIndex: 65,
      });

      return (
        <StyledOverlayWrapper
          delay={showDelay ? speedToUse : 0}
          key={`${overlayAnimationId}-willow`}
          isSinglePage={isSinglePage}
          heightPercentage={sizeInPercent}
          position={position}
        >
          <LottieLayer
            data={(willowToUse as unknown) as Record<string, unknown>}
            autoplay={autoPlay}
            finalFrameValue={900}
          />
        </StyledOverlayWrapper>
      );
    }

    if (overlayAnimationKey === 'overlay-animation-yi-asset') {
      const locketToUse = customizeLocket({
        animationKey: 'overlay-animation-yi-asset',
        initialLayerOne: '.PartnerInitial2',
        initialLayerTwo: '.PartnerInitial1',
        color: colorToUseRGB,
        firstInitial,
        secondInitial,
      });
      return (
        <StyledOverlayWrapper
          delay={showDelay ? speedToUse : 0}
          key={`${overlayAnimationId}-locket`}
          isSinglePage={isSinglePage}
          heightPercentage={sizeInPercent}
          position={position}
          style={{ fill: `#${colorToUseHex}`, stroke: `#${colorToUseHex}` }}
        >
          <LottieLayer
            data={(locketToUse as unknown) as Record<string, unknown>}
            autoplay={autoPlay}
            finalFrameValue={1440}
          />
        </StyledOverlayWrapper>
      );
    }

    if (overlayAnimationKey === 'overlay-animation-bridlewood') {
      const now = new Date();

      const weddingDateToUse = wedding_date || `${now.getFullYear()}/09/21`;
      const bridlewoodToUse = customizeBridlewood(weddingDateToUse, colorToUseRGB);

      return (
        <StyledBridlewoodWrapper
          delay={showDelay ? speedToUse : 0}
          key={`${overlayAnimationId}-Bridlewood`}
          isSinglePage={isSinglePage}
          heightPercentage={sizeInPercent}
          position={position}
          style={{ fill: `#${colorToUseHex}`, stroke: `#${colorToUseHex}` }}
        >
          <LottieLayer data={bridlewoodToUse} autoplay={autoPlay} finalFrameValue={600} />
        </StyledBridlewoodWrapper>
      );
    }

    if (overlayAnimationKey === 'overlay-animation-viall') {
      const viallTopRightToUse = customizeViall(colorToUseRGB, true);
      const viallBottomLeftToUse = customizeViall(colorToUseRGB, false);

      return (
        <>
          <StyledViallTopRightWrapper
            delay={showDelay ? speedToUse : 0}
            key={`${overlayAnimationId}-Viall-top-right`}
            isSinglePage={isSinglePage}
            heightPercentage={sizeInPercent}
            position={position}
            style={{ fill: `#${colorToUseHex}` }}
          >
            <LottieLayer
              data={(viallTopRightToUse as unknown) as Record<string, unknown>}
              autoplay={autoPlay}
              finalFrameValue={695}
            />
          </StyledViallTopRightWrapper>
          <StyledViallBottomLeftWrapper
            delay={showDelay ? speedToUse : 0}
            key={`${overlayAnimationId}-Viall-bottom-left`}
            isSinglePage={isSinglePage}
            heightPercentage={sizeInPercent}
            position={position}
            style={{ fill: `#${colorToUseHex}` }}
          >
            <LottieLayer
              data={(viallBottomLeftToUse as unknown) as Record<string, unknown>}
              autoplay={autoPlay}
              finalFrameValue={695}
            />
          </StyledViallBottomLeftWrapper>
        </>
      );
    }

    return null;
  };

  const animation = getAnimation();

  if (animation && hasBackgroundImage) {
    return <LottieProvider>{animation}</LottieProvider>;
  }

  return null;
};

export default OverlayAnimation;
